import {
  HStack,
  Text,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { set } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "./Dropdown";
import { startOfHourInTimeZone } from "./tzDateUtils";

type DatePickerTimeFieldsProps = {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  timeZone: string;
};

export function DatePickerTimeFields({
  value,
  onChange,
  timeZone,
}: DatePickerTimeFieldsProps) {
  const parts = useMemo(
    () => parseTimePartsInTimeZone(value, timeZone),
    [value, timeZone],
  );

  const [hour, setHour] = useState(parts.hour);
  const [minute, setMinute] = useState(parts.minute);
  const [second, setSecond] = useState(parts.second);
  const [meridiem, setMeridiem] = useState<"AM" | "PM">(parts.meridiem);

  useEffect(() => {
    const next = parseTimePartsInTimeZone(value, timeZone);
    setHour(next.hour);
    setMinute(next.minute);
    setSecond(next.second);
    setMeridiem(next.meridiem);
  }, [value, timeZone]);

  const handleChange = useCallback(
    (
      nextHour: string,
      nextMinute: string,
      nextSecond: string,
      nextMeridiem: "AM" | "PM",
    ) => {
      // prevent a reparse for the decimal character in the seconds field
      // to allow the user to start typing in milliseconds.
      if (/\.$/.test(nextSecond)) {
        return;
      }

      // if the time component was selected before the calendar day, then it
      // will auto-select to the start of the current hour.
      const baseDate = value ?? startOfHourInTimeZone(new Date(), timeZone);

      const next = applyTimePartsToDateWithTimeZone(
        baseDate,
        nextHour,
        nextMinute,
        nextSecond,
        nextMeridiem,
        timeZone,
      );

      if (!next) {
        return;
      }

      onChange(next);
    },
    [onChange, value, timeZone],
  );

  return (
    <VStack align="stretch" spacing={2} pt={3}>
      <HStack spacing={2}>
        <NumberInput
          value={hour}
          allowMouseWheel
          min={1}
          max={12}
          precision={0}
          clampValueOnBlur
          keepWithinRange
          inputMode="numeric"
          isValidCharacter={(char) => /^[0-9]$/.test(char)}
          onChange={(valueString) => {
            setHour(valueString);
            handleChange(valueString, minute, second, meridiem);
          }}
          format={(value) => `${value}`.padStart(2, "0")}
          width="4rem"
          size="sm"
        >
          <NumberInputField textAlign="center" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>

        <Text color="gray.500">:</Text>

        <NumberInput
          value={minute}
          allowMouseWheel
          min={0}
          max={59}
          precision={0}
          clampValueOnBlur
          keepWithinRange
          inputMode="numeric"
          isValidCharacter={(char) => /^[0-9]$/.test(char)}
          onChange={(valueString) => {
            setMinute(valueString);
            handleChange(hour, valueString, second, meridiem);
          }}
          format={(value) => `${value}`.padStart(2, "0")}
          width="4rem"
          size="sm"
        >
          <NumberInputField textAlign="center" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>

        <Text color="gray.500">:</Text>

        <NumberInput
          value={second}
          allowMouseWheel
          min={0}
          max={59.999}
          precision={3}
          step={second.includes(".") ? 0.001 : 1}
          clampValueOnBlur
          keepWithinRange
          inputMode="decimal"
          onChange={(valueString) => {
            // The inc / dec can sometimes add unwanted trailing .000 which needs to be removed
            const normalised = valueString.replace(/\.000$/, "");

            setSecond(normalised);
            handleChange(hour, minute, normalised, meridiem);
          }}
          format={(value) => {
            const stringValue = value.toString();
            const parts = stringValue.match(/^(\d*)(?:\.(\d*))?$/);

            if (!parts) {
              return value;
            }

            const whole = parts[1] ?? "";
            const fraction = parts[2];

            if (stringValue === ".") {
              return "00.";
            }

            if (fraction !== undefined) {
              return `${whole.padStart(2, "0")}.${fraction}`;
            }

            return whole.padStart(2, "0");
          }}
          width={second.includes(".") ? "6rem" : "4rem"}
          transition="max-width 0.15s ease"
          size="sm"
        >
          <NumberInputField textAlign="center" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>

        <Dropdown
          value={meridiem}
          variant="outline"
          ariaLabel="Select AM / PM"
          options={[
            {
              label: "AM",
              value: "AM",
            },
            { label: "PM", value: "PM" },
          ]}
          onChange={(key) => {
            const nextMeridiem = key as "AM" | "PM";
            setMeridiem(nextMeridiem);
            handleChange(hour, minute, second, nextMeridiem);
          }}
        />
      </HStack>
    </VStack>
  );
}

function parseTimePartsInTimeZone(
  value: Date | undefined,
  timeZone: string,
): {
  hour: string;
  minute: string;
  second: string;
  meridiem: "AM" | "PM";
} {
  if (!value) {
    return {
      hour: "12",
      minute: "0",
      second: "0",
      meridiem: "PM",
    };
  }

  const date = utcToZonedTime(value, timeZone);

  const hour24 = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();
  const millisecond = date.getMilliseconds();

  // milliseconds are input as fractional seconds
  const fractionSeconds =
    millisecond !== 0 ?
      `${second}.${millisecond.toString().padStart(3, "0").replace(/0+$/, "")}`
    : `${second}`;

  const meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return {
    hour: hour12.toString(),
    minute: minute.toString(),
    second: fractionSeconds,
    meridiem,
  };
}

function applyTimePartsToDateWithTimeZone(
  baseDate: Date,
  hourRaw: string,
  minuteRaw: string,
  secondRaw: string,
  meridiem: "AM" | "PM",
  timeZone: string,
): Date | undefined {
  if (!hourRaw || !minuteRaw) {
    return;
  }

  const hour12 = Number(hourRaw);
  const minute = Number(minuteRaw);

  const [wholeSecondRaw = "", fractionalSecondRaw = ""] = secondRaw.split(".");
  const second = Number(wholeSecondRaw);

  const millisecond =
    fractionalSecondRaw ? Number(fractionalSecondRaw.padEnd(3, "0")) : 0;

  if (
    !Number.isFinite(hour12) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second) ||
    !Number.isFinite(millisecond)
  ) {
    return undefined;
  }

  if (
    hour12 < 1 ||
    hour12 > 12 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59 ||
    millisecond < 0 ||
    millisecond > 999
  ) {
    return undefined;
  }

  let hour24 = hour12 % 12;
  if (meridiem === "PM") {
    hour24 += 12;
  }

  const zonedDate = utcToZonedTime(baseDate, timeZone);

  const appliedDate = set(zonedDate, {
    hours: hour24,
    minutes: minute,
    seconds: second,
    milliseconds: millisecond,
  });

  return zonedTimeToUtc(appliedDate, timeZone);
}
